import React, { JSX } from 'react';
import { CloseIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { Box, Text, Tooltip } from '@chakra-ui/react';

import { getLocalDateTimeByUtcTimestamp } from '../../../../common/utils';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { DebtTableStatus } from '../model/enums/debt-status.enum';
import { UserDebtItemResponse } from '../model/user-debt-item.response';

type DebtStatusProps = {
  debt: UserDebtItemResponse;
};

export const DebtStatus = ({ debt }: DebtStatusProps) => {
  const t = useTranslations();
  const status: DebtTableStatus = debt.cancellation ? DebtTableStatus.CANCELLED : DebtTableStatus.PENDING;

  const label =
    status === DebtTableStatus.CANCELLED ? t('users_eco_fee.common.cancelled') : t('users_eco_fee.common.pending');

  const statusColorsMap: Record<DebtTableStatus, { bg: string; border: string; color: string }> = {
    pending: { bg: 'orange.50', border: 'orange.800', color: 'orange.800' },
    cancelled: { bg: 'red.50', border: 'red.700', color: 'red.700' },
  };

  const statusIconMap: Record<DebtTableStatus, JSX.Element> = {
    pending: <WarningTwoIcon boxSize={3} />,
    cancelled: <CloseIcon boxSize={3} />,
  };

  const cancelledTooltipLabel =
    status === DebtTableStatus.CANCELLED
      ? t('users_eco_fee.debts.cancelled', {
          '{{date}}': getLocalDateTimeByUtcTimestamp(debt.cancellation?.timestamp),
          '{{admin_email}}': debt.cancellation?.user.email ?? '',
        })
      : undefined;

  const badge = (
    <Box
      border={'1px'}
      borderColor={statusColorsMap[status].border}
      bg={statusColorsMap[status].bg}
      color={statusColorsMap[status].color}
      borderRadius={'12px'}
      px={2}
      display="inline-flex"
      alignItems="center"
      gap={2}
    >
      {statusIconMap[status]}
      <Text as="span">{label}</Text>
    </Box>
  );

  if (status === DebtTableStatus.CANCELLED) {
    return (
      <Tooltip label={cancelledTooltipLabel} placement="top" bg={'blueGrey.800'} hasArrow textAlign={'center'}>
        {badge}
      </Tooltip>
    );
  }

  if (status === DebtTableStatus.PENDING) {
    return (
      <Tooltip
        label={t('users_eco_fee.debts.status_pending_tooltip')}
        placement="top"
        bg={'blueGrey.800'}
        hasArrow
        textAlign={'center'}
      >
        {badge}
      </Tooltip>
    );
  }

  return badge;
};
